"use client";
import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Paperclip, Plus, MessageSquare, Clock, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { useZeno } from "../../hooks/useZeno";
import { useOrganizations } from "../../hooks/useOrganizations";
import Header from "../../components/LandingHeader/LandingHeader";
import Sidebar from "../../components/Sidebar/Sidebar";
import GlassPanel from "../../components/ui/GlassPanel";
import TypingIndicator from "../../components/TypingIndicator/TypingIndicator";

export default function HomeView() {
    // Prueba temporal de markdown
    const markdownTest = `
    # Título de prueba

    **Texto en negrita**

    - Lista 1
    - Lista 2

    [Enlace a Zenit](https://zenitailab.com)
    `;
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [placeholder, setPlaceholder] = useState("");
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showChatHistory, setShowChatHistory] = useState(false);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const chatScrollRef = useRef(null);

    const { messages, loading, isTyping, sendMessage, chatHistories, loadingChats, 
      loadZenoChats, loadConversation, startNewConversation, currentChatHistoryId } = useZeno();

    const [chatError, setChatError] = useState("");
  
  // Obtener currentAccount desde useOrganizations  
  const { currentAccount } = useOrganizations();

  // Asegurar valores seguros
  const safeChatHistories = Array.isArray(chatHistories) ? chatHistories : [];
  const safeLoadingChats = loadingChats || false;

  const phrases = [
    "Ask Zeno to create a prototype...",
    "Build an AI assistant...",
    "Design a workflow automation...",
    "Create a Shopify integration...",
    "Generate a custom dashboard...",
    "Develop a chatbot for your business...",
  ];

  useEffect(() => {
    const currentPhrase = phrases[phraseIndex];
    const typingSpeed = isDeleting ? 30 : 80;
    const pauseTime = isDeleting ? 500 : 2000;

    if (!isDeleting && charIndex === currentPhrase.length) {
      setTimeout(() => setIsDeleting(true), pauseTime);
      return;
    }

    if (isDeleting && charIndex === 0) {
      setIsDeleting(false);
      setPhraseIndex((prev) => (prev + 1) % phrases.length);
      return;
    }

    const timeout = setTimeout(() => {
      setCharIndex((prev) => prev + (isDeleting ? -1 : 1));
      setPlaceholder(
        currentPhrase.substring(0, charIndex + (isDeleting ? -1 : 1))
      );
    }, typingSpeed);

    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, phraseIndex]);

  // Cargar lista de chats al montar el componente
  useEffect(() => {
    loadZenoChats();
  }, [loadZenoChats]);

  // Scroll automático al final cuando hay nuevos mensajes (igual que AssistantsView, con smooth)
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTo({ top: chatScrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + "px";
    }
  }, [message]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim() || loading) return;
    const userMessage = message;
    setMessage("");
    await sendMessage(userMessage);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSidebarSelect = (label) => {
    switch (label) {
      case "Dashboard":
        navigate("/dashboard");
        break;
      case "Projects":
        navigate("/dashboard?view=Projects");
        break;
      case "Avatars":
        navigate("/dashboard?view=Avatars");
        break;
      case "Assistants":
        navigate("/dashboard?view=Assistants");
        break;
      case "Workflows":
        navigate("/dashboard?view=Workflows");
        break;
      case "Connect":
        navigate("/dashboard?view=Integrations");
        break;
      case "Settings":
        navigate("/dashboard?view=Settings");
        break;
      default:
        break;
    }
  };

  // Formatear fecha para mostrar en la lista de chats
  const formatChatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays} days ago`;
    
    return date.toLocaleDateString();
  };

  // Manejar selección de chat desde la historia
  const handleSelectChat = async (chatHistoryId) => {
    try {
      setShowChatHistory(false); // Cerrar sidebar inmediatamente para mejor UX
      setMessage(""); // Limpiar input
      setChatError("");
      const result = await loadConversation(chatHistoryId);
      if (!result?.success) {
        if (result?.status === 404) {
          setChatError("Could not load the conversation. It may have been deleted. Refreshing chat list...");
          await loadZenoChats();
        } else {
          setChatError("Error loading conversation.");
        }
        console.error('Failed to load conversation:', result?.error);
      } else {
        setChatError("");
        // Forzar scroll al cargar conversación
        setTimeout(() => {
          if (chatScrollRef.current) {
            chatScrollRef.current.scrollTo({ top: chatScrollRef.current.scrollHeight, behavior: 'smooth' });
          }
        }, 100);
      }
    } catch (error) {
      setChatError("Unexpected error loading conversation.");
      console.error('Error selecting chat:', error);
    }
  };

  // Iniciar nuevo chat
  const handleNewChat = () => {
    startNewConversation();
    setShowChatHistory(false);
  };

  return (
    <div className="min-h-screen bg-gray-950 relative overflow-hidden">

      <div className="absolute inset-0 pointer-events-none bg-black" />

      <div
        className="absolute pointer-events-none rounded-full opacity-0 animate-fade-in"
        style={{
          top: "1rem",
          left: "4rem",
          width: "33rem",
          height: "33rem",
          background:
            "radial-gradient(circle at 25% 25%, rgba(75,0,130,0.8) 0%, rgba(75,0,130,0.4) 50%, transparent 90%)",
          filter: "blur(140px)",
          animationDelay: '0.2s'
        }}
      />

      <Header />
      <Sidebar active="Home" onSelect={handleSidebarSelect} />

      {/* Modern Chat History Sidebar */}
      {showChatHistory && (
        <div className="fixed inset-0 z-50 flex justify-start">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm opacity-0 animate-fade-in" 
            onClick={() => setShowChatHistory(false)} 
          />
          
          {/* Sidebar Panel */}
          <div className="relative w-96 h-full bg-gray-950/95 backdrop-blur-xl border-r border-gray-700/30 shadow-2xl flex flex-col opacity-0 animate-reveal-left">
            {/* Header */}
            <div className="p-6 border-b border-gray-800/80 flex-shrink-0">
              <div className="flex items-center justify-end mb-6">
                <button
                  onClick={() => setShowChatHistory(false)}
                  className="text-gray-400 hover:text-white hover:bg-white/10 rounded-full transition-all duration-300 w-10 h-10 flex items-center justify-center hover:rotate-90"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* New Chat Button */}
              <button
                onClick={handleNewChat}
                className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-4 py-3 rounded-xl flex items-center justify-center gap-3 transition-all duration-300 shadow-lg hover:shadow-purple-500/25 font-medium hover:scale-[1.02]"
              >
                <Plus className="w-5 h-5" />
                Start New Chat
              </button>
            </div>
            
            {/* Chat List - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {safeLoadingChats ? (
                <div className="space-y-3">
                  {/* Skeleton loading animation */}
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="flex space-x-4 p-4 bg-gray-800/40 rounded-xl border border-gray-700/50 opacity-0 animate-reveal-up [animation-fill-mode:forwards]" style={{ animationDelay: `${i * 0.08}s` }}>
                      <div className="rounded-full bg-gray-700 h-10 w-10 flex-shrink-0 skeleton"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-700 rounded w-3/4 skeleton"></div>
                        <div className="h-3 bg-gray-800 rounded w-1/2 skeleton"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : safeChatHistories.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400 opacity-0 animate-scale-reveal [animation-fill-mode:forwards]">
                  <MessageSquare className="w-12 h-12 mb-4 opacity-30" />
                  <p className="text-center">No conversations yet</p>
                  <p className="text-sm text-gray-500 text-center mt-1">Start chatting with Zeno to see your history here</p>
                </div>
              ) : (
                safeChatHistories.map((chat, index) => (
                  <button
                    key={chat.id}
                    onClick={() => handleSelectChat(chat.id)}
                    className={`group w-full text-left p-4 rounded-xl transition-all duration-300 border hover:shadow-lg cursor-pointer opacity-0 animate-reveal-up ${
                      currentChatHistoryId === chat.id
                        ? 'bg-gradient-to-r from-purple-600/30 to-purple-700/30 border-purple-500/50 shadow-lg shadow-purple-500/10 scale-105'
                        : 'bg-gray-800/40 border-gray-700/50 hover:bg-gray-700/60 hover:border-gray-600 hover:scale-[1.02]'
                    }`}
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                        currentChatHistoryId === chat.id 
                          ? 'bg-purple-500' 
                          : 'bg-gray-700 group-hover:bg-gray-600'
                      }`}>
                        <MessageSquare className={`w-5 h-5 ${
                          currentChatHistoryId === chat.id ? 'text-white' : 'text-gray-400'
                        }`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium text-sm mb-1 line-clamp-2 ${
                          currentChatHistoryId === chat.id ? 'text-white' : 'text-gray-200 group-hover:text-white'
                        }`}>
                          {chat.title || `Chat ${index + 1}`}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />
                          {formatChatDate(chat.updatedAt || chat.createdAt)}
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <main className="relative z-10 flex flex-col min-h-screen px-4 pt-32 pb-12">
        <div
          className={`${messages.length === 0 ? 'w-full max-w-3xl flex flex-col justify-center items-center mx-auto' : 'w-full h-full max-w-3xl flex flex-col mx-auto'}`}
          style={{ height: "calc(100vh - 16rem)" }}
        >

          {chatError && (
            <div className="w-full max-w-2xl mx-auto mb-4 p-4 bg-red-900/80 text-red-200 rounded-xl border border-red-700 text-center">
              {chatError}
            </div>
          )}
          {messages.length === 0 ? (
            <>
              <div className="text-center mb-16 opacity-0 animate-slide-blur [animation-fill-mode:forwards]" style={{ animationDelay: '0.1s' }}>
                <h1 className="text-7xl font-bold text-white tracking-tight mb-6">
                  Build something with Zeno
                </h1>
                <p className="text-gray-400 text-lg max-w-2xl">
                  Create AI agents, workflows, and integrations by chatting with
                  Zeno
                </p>
              </div>

              <div className="relative w-full max-w-3xl mx-auto opacity-0 animate-expand-in" style={{ animationDelay: '0.2s' }}>
                <form onSubmit={handleSubmit}>
                  <GlassPanel className="p-6">
                    <div className="flex items-center gap-4">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-gray-400 hover:text-white hover:bg-white/10 rounded-full flex-shrink-0"
                      >
                        <Paperclip className="w-6 h-6" />
                      </Button>

                      <Textarea
                        ref={textareaRef}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        className="flex-1 bg-transparent border-0 text-white text-lg placeholder:text-gray-500 resize-none min-h-[60px] max-h-[200px] focus:ring-0 focus:outline-none py-3"
                        rows={1}
                      />

                      <button
                        type="button"
                        onClick={() => setShowChatHistory(true)}
                        className="text-gray-400 hover:text-white hover:bg-white/10 rounded-full p-2 flex-shrink-0 transition-colors"
                      >
                        <MessageSquare className="w-6 h-6" />
                      </button>

                      <Button
                        type="submit"
                        disabled={!message.trim() || isTyping}
                        className="bg-purple-600 hover:bg-purple-700 text-white rounded-full h-12 w-12 p-0 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-purple-500/30"
                      >
                        <Send className="w-6 h-6" />
                      </Button>
                    </div>
                  </GlassPanel>
                </form>
              </div>
            </>
          ) : (
            <div className="h-full flex flex-col">
              <div className="flex-1 overflow-y-auto w-full" ref={chatScrollRef}>
                <div className="space-y-6 px-0 py-6">
                {messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex w-full opacity-0 animate-reveal-up ${
                      msg.role === "user"
                        ? "justify-end"
                        : "justify-start"
                    }`}
                    style={{ animationDelay: `${Math.min(index * 0.05, 0.3)}s` }}
                  >
                    <div
                      className={`max-w-xl w-fit ${
                        msg.role === "user"
                          ? "bg-gray-700 text-white rounded-3xl rounded-br-md px-6 py-4 mr-8 mt-2"
                          : "bg-gray-800/50 backdrop-blur-sm text-gray-100 rounded-3xl rounded-bl-md px-6 py-4 border border-gray-700/30 ml-8 mt-2"
                      }`}
                    >
                      {msg.role === "assistant" && (
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-bold">Z</span>
                          </div>
                          <span className="text-sm font-medium text-gray-300">Zeno</span>
                        </div>
                      )}
                      {msg.role === "assistant"
                        ? Array.isArray(msg.contentBlocks)
                          ? msg.contentBlocks.map((cb, i) => {
                              let markdown = '';
                              if (cb.data && typeof cb.data === 'object') {
                                if (typeof cb.data.text === 'string') markdown = cb.data.text;
                                else if (Array.isArray(cb.data)) markdown = cb.data.map(x => typeof x === 'string' ? x : JSON.stringify(x)).join('\n');
                                else markdown = JSON.stringify(cb.data, null, 2);
                              } else if (typeof cb.data === 'string') {
                                markdown = cb.data;
                              }
                              if (!markdown && cb.type) markdown = cb.type;
                              return markdown && markdown.trim() ? (
                                <ReactMarkdown
                                  key={i}
                                  remarkPlugins={[remarkGfm]}
                                  components={{
                                    h1: ({node, ...props}) => <h1 style={{fontSize: '1.5rem', fontWeight: 700, marginBottom: 12, color: '#c7aaff'}} {...props} />,
                                    h2: ({node, ...props}) => <h2 style={{fontSize: '1.25rem', fontWeight: 600, marginBottom: 10, color: '#b48be6'}} {...props} />,
                                    h3: ({node, ...props}) => <h3 style={{fontSize: '1.1rem', fontWeight: 600, marginBottom: 8, color: '#a47fd1'}} {...props} />,
                                    ul: ({node, ...props}) => <ul style={{marginLeft: 24, marginBottom: 12, listStyleType: 'disc'}} {...props} />,
                                    ol: ({node, ...props}) => <ol style={{marginLeft: 24, marginBottom: 12, listStyleType: 'decimal'}} {...props} />,
                                    li: ({node, ...props}) => <li style={{marginBottom: 4}} {...props} />,
                                    strong: ({node, ...props}) => <strong style={{fontWeight: 700}} {...props} />,
                                    p: ({node, ...props}) => <p style={{marginBottom: 12}} {...props} />,
                                  }}
                                >{markdown}</ReactMarkdown>
                              ) : null;
                            })
                          : (typeof msg.content === 'string' && msg.content.trim())
                            ? <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                  h1: ({node, ...props}) => <h1 style={{fontSize: '1.5rem', fontWeight: 700, marginBottom: 12, color: '#c7aaff'}} {...props} />,
                                  h2: ({node, ...props}) => <h2 style={{fontSize: '1.25rem', fontWeight: 600, marginBottom: 10, color: '#b48be6'}} {...props} />,
                                  h3: ({node, ...props}) => <h3 style={{fontSize: '1.1rem', fontWeight: 600, marginBottom: 8, color: '#a47fd1'}} {...props} />,
                                  ul: ({node, ...props}) => <ul style={{marginLeft: 24, marginBottom: 12, listStyleType: 'disc'}} {...props} />,
                                  ol: ({node, ...props}) => <ol style={{marginLeft: 24, marginBottom: 12, listStyleType: 'decimal'}} {...props} />,
                                  li: ({node, ...props}) => <li style={{marginBottom: 4}} {...props} />,
                                  strong: ({node, ...props}) => <strong style={{fontWeight: 700}} {...props} />,
                                  p: ({node, ...props}) => <p style={{marginBottom: 12}} {...props} />,
                                }}
                              >{msg.content}</ReactMarkdown>
                            : (typeof msg.response === 'string' && msg.response.trim())
                              ? <ReactMarkdown
                                  remarkPlugins={[remarkGfm]}
                                  components={{
                                    h1: ({node, ...props}) => <h1 style={{fontSize: '1.5rem', fontWeight: 700, marginBottom: 12, color: '#c7aaff'}} {...props} />,
                                    h2: ({node, ...props}) => <h2 style={{fontSize: '1.25rem', fontWeight: 600, marginBottom: 10, color: '#b48be6'}} {...props} />,
                                    h3: ({node, ...props}) => <h3 style={{fontSize: '1.1rem', fontWeight: 600, marginBottom: 8, color: '#a47fd1'}} {...props} />,
                                    ul: ({node, ...props}) => <ul style={{marginLeft: 24, marginBottom: 12, listStyleType: 'disc'}} {...props} />,
                                    ol: ({node, ...props}) => <ol style={{marginLeft: 24, marginBottom: 12, listStyleType: 'decimal'}} {...props} />,
                                    li: ({node, ...props}) => <li style={{marginBottom: 4}} {...props} />,
                                    strong: ({node, ...props}) => <strong style={{fontWeight: 700}} {...props} />,
                                    p: ({node, ...props}) => <p style={{marginBottom: 12}} {...props} />,
                                  }}
                                >{msg.response}</ReactMarkdown>
                              : (typeof msg.message === 'string' && msg.message.trim())
                                ? <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                      h1: ({node, ...props}) => <h1 style={{fontSize: '1.5rem', fontWeight: 700, marginBottom: 12, color: '#c7aaff'}} {...props} />,
                                      h2: ({node, ...props}) => <h2 style={{fontSize: '1.25rem', fontWeight: 600, marginBottom: 10, color: '#b48be6'}} {...props} />,
                                      h3: ({node, ...props}) => <h3 style={{fontSize: '1.1rem', fontWeight: 600, marginBottom: 8, color: '#a47fd1'}} {...props} />,
                                      ul: ({node, ...props}) => <ul style={{marginLeft: 24, marginBottom: 12, listStyleType: 'disc'}} {...props} />,
                                      ol: ({node, ...props}) => <ol style={{marginLeft: 24, marginBottom: 12, listStyleType: 'decimal'}} {...props} />,
                                      li: ({node, ...props}) => <li style={{marginBottom: 4}} {...props} />,
                                      strong: ({node, ...props}) => <strong style={{fontWeight: 700}} {...props} />,
                                      p: ({node, ...props}) => <p style={{marginBottom: 12}} {...props} />,
                                    }}
                                  >{msg.message}</ReactMarkdown>
                                : <span className="text-gray-400 italic">Sin respuesta</span>
                        : <p className="whitespace-pre-wrap leading-relaxed font-medium">{msg.message || msg.content}</p>
                      }
                    </div>
                  </div>
                ))}

                {isTyping && (
                  <div className="flex justify-start mb-8">
                    <div className="flex items-center gap-3 px-6 py-4 bg-gray-800/50 backdrop-blur-sm rounded-3xl rounded-bl-md border border-gray-700/30">
                      <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">
                          Z
                        </span>
                      </div>
                      <TypingIndicator />
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
                </div>
              </div>
            </div>
          )}

          {messages.length > 0 && (
            <div className="w-full max-w-3xl mx-auto mt-4 flex justify-center">
              <form onSubmit={handleSubmit} className="w-full">
                <GlassPanel className="p-6">
                  <div className="flex items-center gap-4">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-gray-400 hover:text-white hover:bg-white/10 rounded-full flex-shrink-0"
                    >
                      <Paperclip className="w-6 h-6" />
                    </Button>

                    <Textarea
                      ref={textareaRef}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={placeholder}
                      className="flex-1 bg-transparent border-0 text-white text-lg placeholder:text-gray-500 resize-none min-h-[60px] max-h-[200px] focus:ring-0 focus:outline-none py-3"
                      rows={1}
                    />

                    <button
                      type="button"
                      onClick={() => setShowChatHistory(true)}
                      className="text-gray-400 hover:text-white hover:bg-white/10 rounded-full p-2 flex-shrink-0 transition-all duration-300 hover:scale-110"
                    >
                      <MessageSquare className="w-6 h-6" />
                    </button>

                    <Button
                      type="submit"
                      disabled={!message.trim() || isTyping}
                      className="bg-purple-600 hover:bg-purple-700 text-white rounded-full h-12 w-12 p-0 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-purple-500/30"
                    >
                      <Send className="w-6 h-6" />
                    </Button>
                  </div>
                </GlassPanel>
              </form>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
